/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import {
  AALocaleProvider,
  useAALocale,
} from '@airasia-phoenix/react-native/utils/AALocaleContext';
import { AALocale } from '@airasia-phoenix/react-native/utils';
import { Logger, LoggerProvider } from '@airasia-rn/core-logger';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import { QueryClientProvider } from '@tanstack/react-query';
import { persistQueryClient } from '@tanstack/react-query-persist-client';
import { changeLanguage } from 'i18next';
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Appearance, useColorScheme } from 'react-native';
import { UnistylesRuntime } from 'react-native-unistyles';
import {
  buildEmptyNamespace,
  DEFAULT_LANGUAGE,
  initI18n,
  LocaleProvider,
  SupportedLocales,
} from '../localization';
import { getQueryClient } from './queryClient';
import { TranslationResources } from '../localization/LocaleProvider';
import { darkTheme, lightTheme, pd } from '../utils';
import { DEFAULT_CURRENCY, SupportedCurrencies } from './currencies';
import { CurrencyProvider } from './CurrencyProvider';
import { initThemes } from './unistyles';
// import { Platform } from 'react-native';
// import { createSQLitePersister } from './sqlitePersister';

export const QUERY_PERSIST_CACHE = 'persist_cache';

initThemes();

/**
 * User-facing theme preference.
 * - `System` (default): follow the OS light/dark setting and react to changes.
 * - `Light` / `Dark`: force that theme regardless of the OS setting.
 */
export enum ThemeMode {
  System = 'system',
  Light = 'light',
  Dark = 'dark',
}

/** Type guard for a persisted / host-provided theme preference value. */
export const isThemeMode = (value: unknown): value is ThemeMode =>
  value === ThemeMode.System ||
  value === ThemeMode.Light ||
  value === ThemeMode.Dark;

/** Resolve the effective dark/light boolean from the preference + OS scheme. */
const resolveIsDark = (
  mode: ThemeMode,
  systemScheme: 'light' | 'dark' | null | undefined,
): boolean =>
  mode === ThemeMode.System ? systemScheme === 'dark' : mode === ThemeMode.Dark;

export type AppConfig = {
  appName: string;
  appVersion: string;
  env: 'stg' | 'prd';
  deviceId: string;
  locale?: SupportedLocales;
  currency: SupportedCurrencies;
  themeMode?: ThemeMode;
  onThemeModeChange?: (mode: ThemeMode) => void;
  onCurrencyChange?: (currency: SupportedCurrencies) => void;
  onLocaleChange?: (locale: SupportedLocales) => void;
  deeplink?: string;
  dvToken?: string;
  bundledTranslations?: TranslationResources;
  nsUrlMap?: Record<string, string>;
  onlyBundledTranslations?: boolean;
  translationsVersion?: number;
  logger?: Logger;
  geoId?: string;
  gaId?: string;
};

type AppConfigContextType = {
  config: AppConfig;
  /** Current theme preference (`system` | `light` | `dark`). */
  themeMode: ThemeMode;
  /** Set (and persist) the theme preference. */
  setThemeMode: (mode: ThemeMode) => void;
  toggleDarkMode: () => void;
  clearPersistQueryClient?: () => void;
  updateConfig: (newConfig: Partial<AppConfig>) => void;
};

export const AppConfigContext = createContext<AppConfigContextType | undefined>(
  undefined,
);

type AppConfigProviderProps = AppConfig & {
  children: ReactNode;
};

const updateTheme = (darkMode: boolean) => {
  if (darkMode) {
    pd.setTheme(darkTheme);
    UnistylesRuntime.setTheme('dark');
  } else {
    pd.setTheme(lightTheme);
    UnistylesRuntime.setTheme('light');
  }
};

// AALocaleProvider starts at EN_GB and takes no locale prop, so the app locale
// has to be pushed in with setLocale from a descendant.
const AALocaleSync = ({ locale }: { locale?: string }) => {
  const { setLocale } = useAALocale();

  // setLocale is rebuilt on every provider render, so keep the latest in a ref
  // rather than depending on it and re-running on every render.
  const setLocaleRef = useRef(setLocale);
  useEffect(() => {
    setLocaleRef.current = setLocale;
  }, [setLocale]);

  useEffect(() => {
    if (!locale) return;
    // fromString normalises en-gb / en_GB / enGB and falls back to EN_GB.
    setLocaleRef.current(AALocale.fromString(locale));
  }, [locale]);

  return null;
};

export const AppConfigProvider: React.FC<AppConfigProviderProps> = ({
  children,
  ...appConfig
}) => {
  // Initial theme preference: explicit `themeMode` prop wins, otherwise Light.
  const initialThemeMode: ThemeMode = appConfig.themeMode ?? ThemeMode.Light;

  const [themeMode, setThemeModeState] = useState<ThemeMode>(initialThemeMode);

  // Live OS colour scheme; re-renders when the user flips the device setting.
  const systemScheme = useColorScheme();
  const isDark = resolveIsDark(themeMode, systemScheme);

  const [config, setConfig] = useState<AppConfig>(() => {
    // Sync apply on init so the first paint uses the right theme.
    updateTheme(resolveIsDark(initialThemeMode, Appearance.getColorScheme())); // 👈 sync apply on init
    return appConfig;
  });

  const updateConfig = useCallback((updates: Partial<AppConfig>) => {
    setConfig((prev) => ({ ...prev, ...updates }));
  }, []);

  // Keep the local preference in sync when the host updates the seed prop
  // (e.g. after it hydrates the persisted value from storage). Mirrors the
  // currency/gaId sync effects below.
  useEffect(() => {
    if (appConfig.themeMode == undefined) return;
    setThemeModeState((prev) =>
      prev === appConfig.themeMode ? prev : appConfig.themeMode!,
    );
  }, [appConfig.themeMode]);

  // Apply immediately for a responsive UI, then let the host persist the choice
  // via the callback (mirrors onCurrencyChange / onLocaleChange).
  const { onThemeModeChange } = appConfig;
  const setThemeMode = useCallback(
    (mode: ThemeMode) => {
      setThemeModeState(mode);
      onThemeModeChange?.(mode);
    },
    [onThemeModeChange],
  );

  // Apply the resolved theme whenever the preference or OS scheme changes.
  // In `system` mode this fires when the user toggles the device appearance.
  useEffect(() => {
    updateTheme(isDark);
  }, [isDark]);

  // update locale from hosting app or storybook
  const prevLocalePropRef = useRef(appConfig.locale);
  useEffect(() => {
    if (
      appConfig.locale == undefined ||
      appConfig.locale === prevLocalePropRef.current
    )
      return;
    prevLocalePropRef.current = appConfig.locale;
    changeLanguage(appConfig.locale)
      .then(() => {
        updateConfig({ locale: appConfig.locale });
      })
      .catch((error) => {
        console.error('Failed to apply language from prop:', error);
      });
  }, [appConfig.locale, updateConfig]);

  useEffect(() => {
    if (appConfig.currency !== undefined) {
      setConfig((prev) => {
        if (prev.currency !== appConfig.currency) {
          return { ...prev, currency: appConfig.currency };
        }
        return prev;
      });
    }
  }, [appConfig.currency]);

  useEffect(() => {
    if (appConfig.gaId !== undefined) {
      setConfig((prev) => {
        if (prev.gaId !== appConfig.gaId) {
          return { ...prev, gaId: appConfig.gaId };
        }
        return prev;
      });
    }
  }, [appConfig.gaId]);

  const toggleDarkMode = () => {
    const nextIsDark = !isDark;
    // Toggling is an explicit choice, so break out of `system` into light/dark.
    setThemeMode(nextIsDark ? ThemeMode.Dark : ThemeMode.Light);
  };

  useEffect(() => {
    initI18n(
      config.env,
      config.onlyBundledTranslations ?? process.env.NODE_ENV !== 'development',
      config.translationsVersion,
      config.bundledTranslations,
      config.nsUrlMap,
    );
  }, [
    config.bundledTranslations,
    config.onlyBundledTranslations,
    config.env,
    config.translationsVersion,
    config.nsUrlMap,
  ]);

  const handleCurrencyChange = useCallback((currency: SupportedCurrencies) => {
    setConfig((prev) => {
      if (prev.currency !== currency) {
        prev.onCurrencyChange?.(currency);
      }
      return { ...prev, currency };
    });
  }, []);

  // Lazy `useState` initialiser, not `useMemo`: useMemo is a performance hint
  // React may discard, whereas a state initialiser is guaranteed to run once per
  // mount. Paired with the module singleton in getQueryClient, the client also
  // survives remounts of this provider.
  const [queryClient] = useState(getQueryClient);
  const persister = useMemo(() => {
    const queryPersister = createAsyncStoragePersister({
      storage: AsyncStorage,
    });
    setConfig((prev) => ({ ...prev, queryPersister }));
    return queryPersister;
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const [unsubscribe] = persistQueryClient({
      queryClient,
      persister,
      maxAge: 1000 * 60 * 60 * 24 * 7, // 7 days, we might need to make it 6 months or 1 year
      dehydrateOptions: {
        shouldDehydrateQuery: (query) =>
          query.state.status === 'success' &&
          query.queryKey.some(
            (key) =>
              typeof key === 'string' && key.includes(QUERY_PERSIST_CACHE),
          ),
      },
    });

    return unsubscribe;
  }, [queryClient, persister]);

  const clearPersistQueryClient = () => {
    persister.removeClient();
  };

  return (
    <AppConfigContext.Provider
      value={{
        config,
        themeMode,
        setThemeMode,
        updateConfig,
        toggleDarkMode,
        clearPersistQueryClient,
      }}
    >
      <LocaleProvider
        namespace={'common'}
        initialLocale={config.locale ?? DEFAULT_LANGUAGE}
        translationResources={
          config.bundledTranslations ?? buildEmptyNamespace('common')
        }
        onLocaleChange={appConfig.onLocaleChange}
      >
        <AALocaleProvider>
          <AALocaleSync locale={config.locale} />
          <QueryClientProvider client={queryClient}>
            <LoggerProvider logger={appConfig.logger}>
              <CurrencyProvider
                initialCurrency={config.currency ?? DEFAULT_CURRENCY}
                onCurrencyChange={handleCurrencyChange}
              >
                {children}
              </CurrencyProvider>
            </LoggerProvider>
          </QueryClientProvider>
        </AALocaleProvider>
      </LocaleProvider>
    </AppConfigContext.Provider>
  );
};

export const useAppConfig = (): AppConfigContextType => {
  const ctx = useContext(AppConfigContext);
  if (!ctx)
    throw new Error('useAppConfig must be used inside AppConfigProvider');
  return ctx;
};
